<template>
  <div class="p-8">
    <header class="mb-6">
      <h2 class="text-2xl font-bold mb-4">Categories Management</h2>
      <button 
        @click="addCategory" 
        class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
      >
        Add Category
      </button>
    </header>

    <div v-if="categories.length === 0" class="text-center py-8 text-gray-500">
      No categories available. Add your first category to get started.
    </div>

    <table v-else class="min-w-full bg-white text-gray-900 rounded shadow">
      <thead>
        <tr>
          <th class="px-4 py-2 text-left">Category Name</th>
          <th class="px-4 py-2 text-left">Stakeholders Count</th>
          <th class="px-4 py-2 text-left">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="category in categories" :key="category.id">
          <!-- Category Name (Editable) -->
          <td class="border px-4 py-2">
            <EditableField
              :value="category.name"
              :is-editing="isEditing(category.id, 'name')"
              @start-edit="startEdit(category.id, 'name', category.name)"
              @save="saveEdit(category.id, 'name', $event)"
              @cancel="cancelEdit"
            />
          </td>

          <!-- Stakeholders Count -->
          <td class="border px-4 py-2">
            <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              {{ getStakeholdersCount(category.id) }}
            </span>
          </td>

          <!-- Actions -->
          <td class="border px-4 py-2 text-center">
            <button 
              @click="deleteCategory(category.id)"
              :disabled="getStakeholdersCount(category.id) > 0"
              :class="[
                'px-3 py-1 rounded transition-colors',
                getStakeholdersCount(category.id) > 0 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-red-600 text-white hover:bg-red-700'
              ]"
              :title="getStakeholdersCount(category.id) > 0 ? 'Cannot delete category with stakeholders' : 'Delete category'"
            >
              Delete
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Error Message -->
    <div v-if="error" class="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { createStakeholderMapStore } from '../stores';
import type { CategoryData } from '../../../stakeholdersMap/types/CategoryData';
import EditableField from './EditableField.vue';
import { CreateCategoryData } from '../../../stakeholdersMap/types/CreateCategoryData';

// Types
type EditableField = 'name';

interface EditingState {
  id: string;
  field: EditableField;
}

// Store setup
const stakeholderMapStore = createStakeholderMapStore();
const { getAllCategories, getAllStakeholders } = storeToRefs(stakeholderMapStore);

// Reactive state
const editingCategory = ref<EditingState | null>(null);
const editingValue = ref<string>('');
const error = ref<string>('');

// Computed properties
const categories = computed(() => getAllCategories.value);
const stakeholders = computed(() => getAllStakeholders.value);

// Methods
const isEditing = (id: string, field: EditableField): boolean => {
  return editingCategory.value?.id === id && editingCategory.value?.field === field;
};

const startEdit = (id: string, field: EditableField, value: string): void => {
  editingCategory.value = { id, field };
  editingValue.value = value;
  
  nextTick(() => {
    const editInput = document.querySelector('.edit-input') as HTMLInputElement;
    if (editInput) {
      editInput.focus();
      editInput.select();
    }
  });
};

const saveEdit = async (categoryId: string, field: EditableField, value: string): Promise<void> => {
  if (!value.trim()) {
    error.value = 'Category name cannot be empty';
    return;
  }

  const category = stakeholderMapStore.getCategoryById(categoryId);
  if (!category) {
    error.value = 'Category not found';
    return;
  }

  try {
    error.value = '';
    const updatedCategory: CategoryData = { 
      ...category, 
      [field]: value.trim()
    };
    
    await stakeholderMapStore.updateCategory(updatedCategory);
    editingCategory.value = null;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to update category';
  }
};

const cancelEdit = (): void => {
  editingCategory.value = null;
  error.value = '';
};

const addCategory = async (): Promise<void> => {
  try {
    error.value = '';
    const newCategory = stakeholderMapStore.createDefaultCategory();
    await stakeholderMapStore.addCategory(newCategory);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to add category';
  }
};

const deleteCategory = async (categoryId: string): Promise<void> => {
  if (getStakeholdersCount(categoryId) > 0) {
    error.value = 'Cannot delete category that contains stakeholders';
    return;
  }

  try {
    error.value = '';
    await stakeholderMapStore.deleteCategory(categoryId);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to delete category';
  }
};

const getStakeholdersCount = (categoryId: string): number => {
  return stakeholderMapStore.getStakeholdersCountByCategory(categoryId);
};
</script>
